import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Mail, Building, Globe, MessageSquare, Send, Loader2, CheckCircle,
  HelpCircle, Megaphone, Newspaper, Handshake, Store, Mic, Paintbrush, ArrowLeft
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ContactPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    organization: "",
    email: "",
    country: "",
    interest: "",
    message: "",
    website: "" // Honeypot field - should remain empty for real users
  });

  const interestOptions = [
    { value: "general", label: "General Question", icon: HelpCircle, color: "text-blue-500" },
    { value: "advertising", label: "Advertising / Partnerships", icon: Megaphone, color: "text-amber-500" },
    { value: "press", label: "Press / Media", icon: Newspaper, color: "text-purple-500" },
    { value: "sponsorship", label: "Sponsorship Opportunities", icon: Handshake, color: "text-green-500" },
    { value: "wholesale", label: "Wholesale / Bulk Licensing", icon: Store, color: "text-cyan-500" },
    { value: "speaking", label: "Speaking / Workshop Requests", icon: Mic, color: "text-pink-500" },
    { value: "custom_design", label: "Custom Garden Design", icon: Paintbrush, color: "text-orange-500" },
    { value: "other", label: "Other", icon: Mail, color: "text-gray-500" }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If honeypot is filled, silently reject (it's a bot)
    if (form.website) {
      setSubmitted(true);
      return;
    }
    
    if (!form.name || !form.email || !form.interest || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error("Too many submissions. Please try again later.");
      } else {
        toast.error("Failed to send message. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-amber-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <Card className="max-w-md text-center">
            <CardContent className="p-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Thank You!</h2>
              <p className="text-muted-foreground mb-4">
                Your message has been received. We&apos;ll get back to you within 48 hours.
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                A confirmation email has been sent to your inbox.
              </p>
              <Button onClick={() => navigate("/")} data-testid="contact-return-home-btn">
                Return to Home
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-amber-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/")} data-testid="contact-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
                <Mail className="w-6 h-6 text-primary" />
                Contact Us
              </h1>
              <p className="text-sm text-muted-foreground">
                We&apos;d love to hear from you
              </p>
            </div>
          </div>
          <Link to="/">
            <img src="/logo.png" alt="RootSpace" className="h-8 w-auto" />
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Quick Category Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {interestOptions.slice(0, 4).map((opt) => {
              const Icon = opt.icon;
              return (
                <button
                  key={opt.value}
                  onClick={() => setForm(f => ({ ...f, interest: opt.value }))}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    form.interest === opt.value 
                      ? 'border-primary bg-primary/5' 
                      : 'border-transparent bg-white hover:border-gray-200'
                  }`}
                  data-testid={`contact-category-${opt.value}`}
                >
                  <Icon className={`w-6 h-6 mb-2 ${opt.color}`} />
                  <span className="text-sm font-medium">{opt.label}</span>
                </button>
              );
            })}
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Get in Touch</CardTitle>
              <p className="text-muted-foreground">
                Questions about RootSpace? Want to advertise, partner, or hire us for a speaking engagement? 
                We&apos;re here to help!
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot field - hidden from humans, bots will fill it */}
                <div className="absolute opacity-0 pointer-events-none" aria-hidden="true" style={{ position: 'absolute', left: '-9999px' }}>
                  <label htmlFor="website">Website</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    value={form.website}
                    onChange={(e) => setForm(f => ({ ...f, website: e.target.value }))}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={form.name}
                      onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="Your name"
                      required
                      data-testid="contact-name-input"
                    />
                  </div>

                  {/* Organization */}
                  <div className="space-y-2">
                    <Label htmlFor="organization">
                      <Building className="w-4 h-4 inline mr-1" />
                      Organization (optional)
                    </Label>
                    <Input
                      id="organization"
                      value={form.organization}
                      onChange={(e) => setForm(f => ({ ...f, organization: e.target.value }))}
                      placeholder="Your company or organization"
                      data-testid="contact-organization-input"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                      placeholder="your@email.com"
                      required
                      data-testid="contact-email-input"
                    />
                  </div>

                  {/* Country */}
                  <div className="space-y-2">
                    <Label htmlFor="country">
                      <Globe className="w-4 h-4 inline mr-1" />
                      Country (optional)
                    </Label>
                    <Input
                      id="country"
                      value={form.country}
                      onChange={(e) => setForm(f => ({ ...f, country: e.target.value }))}
                      placeholder="Your country"
                      data-testid="contact-country-input"
                    />
                  </div>
                </div>

                {/* Interest Dropdown */}
                <div className="space-y-2">
                  <Label htmlFor="interest">What can we help you with? *</Label>
                  <select
                    id="interest"
                    value={form.interest}
                    onChange={(e) => setForm(f => ({ ...f, interest: e.target.value }))}
                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                    required
                    data-testid="contact-interest-select"
                  >
                    <option value="">Select a category...</option>
                    {interestOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Category-specific helpful hints */}
                {form.interest === "advertising" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="p-4 bg-amber-50 rounded-lg border border-amber-200"
                  >
                    <p className="text-sm text-amber-800">
                      <strong>Advertising & Partnerships:</strong> We offer banner ads, sponsored content, 
                      newsletter features, and affiliate partnerships. Let us know your goals and budget!
                    </p>
                  </motion.div>
                )}

                {form.interest === "sponsorship" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="p-4 bg-green-50 rounded-lg border border-green-200"
                  >
                    <p className="text-sm text-green-800">
                      <strong>Sponsorship:</strong> Sponsor our monthly GROWTH challenges, educational content, 
                      or community events. Great for brands aligned with sustainability and education.
                    </p>
                  </motion.div>
                )}

                {form.interest === "wholesale" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="p-4 bg-cyan-50 rounded-lg border border-cyan-200"
                  >
                    <p className="text-sm text-cyan-800">
                      <strong>Wholesale & Licensing:</strong> Schools, nurseries, and garden centers can get 
                      bulk access to our premium features. Contact us for volume pricing.
                    </p>
                  </motion.div>
                )}

                {form.interest === "speaking" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="p-4 bg-pink-50 rounded-lg border border-pink-200"
                  >
                    <p className="text-sm text-pink-800">
                      <strong>Speaking & Workshops:</strong> Book us for conferences, schools, garden clubs, 
                      or corporate sustainability events. Virtual and in-person options available.
                    </p>
                  </motion.div>
                )}

                {form.interest === "custom_design" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="p-4 bg-orange-50 rounded-lg border border-orange-200"
                  >
                    <p className="text-sm text-orange-800">
                      <strong>Custom Garden Design:</strong> Want a personalized garden plan created by our experts? 
                      Tell us about your space, goals, and budget for a custom quote.
                    </p>
                  </motion.div>
                )}

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message">
                    <MessageSquare className="w-4 h-4 inline mr-1" />
                    Your Message *
                  </Label>
                  <Textarea
                    id="message"
                    value={form.message}
                    onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                    placeholder="Tell us more about what you're looking for..."
                    className="min-h-[150px]"
                    required
                    data-testid="contact-message-textarea"
                  />
                </div>

                <Button type="submit" className="w-full" disabled={loading} data-testid="contact-submit-btn">
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Additional Contact Info */}
          <div className="mt-8 text-center">
            <p className="text-muted-foreground text-sm mb-4">
              Prefer email? Reach us directly at{" "}
              <a href="mailto:thegardener@rootspace.pro" className="text-primary hover:underline font-medium">
                thegardener@rootspace.pro
              </a>
            </p>
            <div className="flex justify-center gap-4 text-sm text-muted-foreground">
              <span>Response time: Within 48 hours</span>
              <span>•</span>
              <span>Based in the US Virgin Islands</span>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default ContactPage;
